import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

type Choice = 'granted' | 'denied';

/** Also read by the inline script in src/index.html, to apply a saved choice before Analytics starts. */
const STORAGE_KEY = 'fai-analytics-consent';

/** Time zones in the EU/EEA, UK and Switzerland (and a few neighbours), for when the server can't tell from the IP. */
const consentTimeZone = (zone: string) =>
  zone.startsWith('Europe/') ||
  [
    'Atlantic/Reykjavik',
    'Atlantic/Canary',
    'Atlantic/Madeira',
    'Atlantic/Azores',
    'Atlantic/Faroe',
  ].includes(zone);

/**
 * Analytics consent for visitors in the EU, EEA, UK and Switzerland. Google
 * Analytics starts with cookies denied for them (src/index.html); this asks,
 * remembers the answer, and tells Analytics. Add ?force_consent_banner=true to
 * any URL to show the banner anywhere, whatever was chosen before.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private http = inject(HttpClient);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** The visitor is somewhere that needs consent, so offer a way to change it. */
  readonly required = signal(false);
  readonly bannerOpen = signal(false);

  async init() {
    if (!this.isBrowser) return;
    const forced = new URLSearchParams(location.search).get('force_consent_banner') === 'true';
    if (forced) {
      this.required.set(true);
      this.bannerOpen.set(true);
      return;
    }
    const saved = this.saved();
    const region = await firstValueFrom(
      this.http.get<{ required: boolean | null }>('/api/consent'),
    ).catch(() => null);
    const required =
      region?.required ?? consentTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone ?? '');
    this.required.set(required || saved !== null);
    if (required && saved === null) this.bannerOpen.set(true);
  }

  choose(choice: Choice) {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // Storage blocked: the choice applies to this page view only.
    }
    window.gtag?.('consent', 'update', { analytics_storage: choice });
    this.bannerOpen.set(false);
  }

  private saved(): Choice | null {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return value === 'granted' || value === 'denied' ? value : null;
    } catch {
      return null;
    }
  }
}

@Component({
  selector: 'app-consent-banner',
  imports: [RouterLink],
  styles: `
    .banner {
      position: fixed;
      left: 1rem;
      right: 1rem;
      bottom: 1rem;
      z-index: 100;
      max-width: 560px;
      margin: 0 auto;
      padding: 1rem 1.25rem;
      border: 1px solid var(--fai-color-border);
      border-radius: 12px;
      background: var(--fai-color-surface);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    }
    p {
      margin-bottom: 0.75rem;
    }
    .actions {
      display: flex;
      gap: 0.5rem;
      justify-content: flex-end;
    }
    .actions button {
      flex: 0 1 8rem;
    }
  `,
  template: `
    @if (consent.bannerOpen()) {
      <div class="banner" role="dialog" aria-label="Analytics cookies">
        <p>
          May we use Google Analytics cookies to see how Friendlai is used? They're not used for
          ads. You can change this any time from "Cookie settings" at the bottom of the page.
          <a routerLink="/privacy">Privacy</a>
        </p>
        <div class="actions">
          <button (click)="consent.choose('denied')">Decline</button>
          <button (click)="consent.choose('granted')">Accept</button>
        </div>
      </div>
    }
  `,
})
export class ConsentBanner {
  consent = inject(ConsentService);
}
